import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import clsx from "clsx";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Bot, ArrowLeft, Globe, Rocket, KeyRound, Star, TrendingUp } from "lucide-react";

const riskColor = (r) => ({ Low: "bg-pos/15 text-pos", Medium: "bg-gold/15 text-gold", High: "bg-neg/15 text-neg" }[r] || "bg-fx-s2 text-fx-s3");

function Logo({ url, className = "h-14 w-14", iconClass = "h-7 w-7" }) {
  if (url) return <img src={url} alt="" className={clsx(className, "rounded-xl object-cover bg-fx-s2")} />;
  return <div className={clsx(className, "rounded-xl bg-fx-s2 grid place-items-center text-brandblue")}><Bot className={iconClass} /></div>;
}

export default function VendorProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/bots/vendors/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="text-center py-20" data-testid="vendor-not-found">
      <p className="text-fx-s3">Vendor not found.</p>
      <Button onClick={() => navigate("/bots")} className="mt-4 bg-brandblue hover:bg-blue-500">Back to marketplace</Button>
    </div>
  );
  if (!data) return <div className="text-fx-s3 py-20 text-center">Loading vendor…</div>;

  return (
    <div className="space-y-6" data-testid="vendor-profile">
      <button onClick={() => navigate("/bots")} data-testid="vendor-back" className="flex items-center gap-2 text-sm text-fx-s3 hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Featured Trading Bots
      </button>

      <div className="rounded-2xl bg-fx-s1 border border-white/10 p-6 flex flex-col sm:flex-row gap-5 items-start">
        <Logo url={data.logo_url} className="h-20 w-20" iconClass="h-10 w-10" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-2xl font-extrabold" data-testid="vendor-name">{data.name}</h1>
            <span className="text-xs text-fx-s3 bg-fx-base px-2 py-0.5 rounded-full">{data.bot_count} bot{data.bot_count === 1 ? "" : "s"}</span>
          </div>
          <p className="text-sm text-fx-s3 mt-2 max-w-2xl" data-testid="vendor-bio">{data.bio || "This vendor lists AI trading strategies on the Fx Cloud marketplace."}</p>
          {data.website && (
            <a href={data.website} target="_blank" rel="noreferrer" data-testid="vendor-website" className="inline-flex items-center gap-1.5 text-sm text-brandblue hover:underline mt-3">
              <Globe className="h-4 w-4" /> {data.website.replace(/^https?:\/\//, "")}
            </a>
          )}
        </div>
      </div>

      <h2 className="text-xs font-bold uppercase tracking-wider text-fx-s3">Strategies by {data.name}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.bots.map((b) => (
          <div key={b.id} data-testid={`vendor-bot-${b.id}`} className="rounded-xl bg-fx-s1 border border-white/5 p-5 flex flex-col relative">
            {b.featured && <span className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gold/15 text-gold border border-gold/30"><Star className="h-3 w-3 fill-gold" /> Featured</span>}
            <div className="flex items-center gap-3">
              <Logo url={b.logo_url} className="h-12 w-12" iconClass="h-6 w-6" />
              <div className="min-w-0">
                <h3 className="font-semibold truncate">{b.name}</h3>
                <span className={clsx("inline-block mt-1 px-2 py-0.5 rounded text-[11px] font-mono", riskColor(b.risk_level))}>{b.risk_level} risk</span>
              </div>
            </div>
            <p className="text-sm text-fx-s3 mt-3 flex-1">{b.short_description || b.description}</p>
            {b.performance && (
              <div className="mt-3 rounded-lg bg-fx-base border border-white/5 px-3 py-2 text-xs">
                {b.performance.has_data ? (
                  <div className="flex items-center justify-between">
                    <span className="text-fx-s3">Win {b.performance.win_rate}% · {b.performance.total_trades} trades</span>
                    <span className={clsx("font-mono font-semibold", b.performance.roi_pct >= 0 ? "text-pos" : "text-neg")}>{b.performance.roi_pct >= 0 ? "+" : ""}{b.performance.roi_pct}%</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-fx-s3"><TrendingUp className="h-3.5 w-3.5" /> No live data yet</div>
                )}
              </div>
            )}
            <Button onClick={() => navigate("/bots")} data-testid={`vendor-bot-view-${b.id}`} className="mt-4 bg-brandblue hover:bg-blue-500 gap-2">
              {b.installed ? <><Rocket className="h-4 w-4" /> Installed</> : <><KeyRound className="h-4 w-4" /> View in Marketplace</>}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
